import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ImageStepComponent } from 'api/types';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import MessageContainer from 'components/MessageContainer';
import type React from 'react';
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { setImageViewer } from 'actions/main';
import { postMessage } from 'utils/postMessage';
import {
  selectIsAuthorIconVisible,
  selectIsScrollAtTheBottom,
} from 'reducers/workflows';
import { scrollToTheBottom } from 'utils/helpers';

interface Props {
  isLastStep: boolean;
  stepIndex: number;
  widgetComponent: ImageStepComponent;
}

export const ImageStep: React.FC<React.PropsWithChildren<Props>> = ({
  stepIndex,
  widgetComponent,
}) => {
  const images = widgetComponent.component_fields.images;
  const dispatch = useAppDispatch();
  const isScrollAtTheBottom = useAppSelector(selectIsScrollAtTheBottom);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );

  /**
   * Image size is unknown before it loads, so we need to trigger
   * `scrollToTheBottom()` on load.
   */
  const handleLoad = useCallback(() => {
    if (isScrollAtTheBottom) {
      scrollToTheBottom('smooth');
    }
  }, [isScrollAtTheBottom]);

  return images.map(image => (
    <MessageContainer key={image.url}>
      <StyledAgathaMessage
        isAuthorIconVisible={isAuthorIconVisible}
        stepIndex={stepIndex}
      >
        <Box
          component='button'
          onClick={() => {
            postMessage({
              event: 'forethoughtWidgetViewImage',
              open: true,
            });
            dispatch(
              setImageViewer({
                alt: image.description,
                src: decodeURIComponent(image.url),
              }),
            );
          }}
          sx={{
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <Box
            alt={image.description}
            component='img'
            onLoad={handleLoad}
            src={decodeURIComponent(image.url)}
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              objectFit: 'contain',
            }}
          />
        </Box>
      </StyledAgathaMessage>
    </MessageContainer>
  ));
};

const StyledAgathaMessage = styled(AgathaMessage)`
  padding: 0px;
  background-color: transparent;
  margin-right: 0px;
`;
